import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportList, RecordLength } from '../../data/Constants';
import { CardLayout } from '../Layouts/CardLayout';
import { reportDownloadRegistry } from '../../api/ReportApi';

/**
 * Renders a records-per-page selector control.
 *
 * @param recordsPerPage - The currently selected number of records per page.
 * @param handleChange - Callback invoked when the user selects a new value.
 * @param t - i18next translation function.
 * @returns A JSX element containing the label and {@link Select} dropdown.
 */
const recordLengthPicker = (
  recordsPerPage: number,
  handleChange: (event: SelectChangeEvent<number>) => void,
  t: (value: string) => string
) => {
  return (
    <div className="d-flex align-center recordPicker">
      {t('labels.recordsPerPage') + ':'}&emsp;
      <FormControl size="small" style={{ width: '5.5rem' }}>
        <Select
          id="recordsPerPage"
          name="recordsPerPage"
          value={recordsPerPage}
          onChange={handleChange}
        >
          {RecordLength.map((range) => (
            <MenuItem key={range} value={range}>
              {range}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

/**
 * Page-level component that displays the full list of downloadable reports.
 *
 * Features:
 * - Paginates the {@link reportList} based on the selected records-per-page value.
 * - Renders each report as a {@link CardLayout} card.
 * - Dispatches the appropriate download handler from {@link reportDownloadRegistry}
 *   when the user clicks the download button on a card.
 */
export const ReportsManagement = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RecordLength[0]);

  /**
   * Looks up and invokes the download handler registered under `downloadFunctionName`.
   * Logs a warning if no matching handler is found.
   *
   * @param downloadFunctionName - Key used to look up the handler in {@link reportDownloadRegistry}.
   */
  const handleDownload = (downloadFunctionName: string) => {
    const downloadHandler = reportDownloadRegistry[downloadFunctionName];
    if (downloadHandler) {
      downloadHandler();
    } else {
      console.warn(`No export handler found for: ${downloadFunctionName}`);
    }
  };

  const pageCount = Math.ceil(reportList.length / recordsPerPage) || 1;
  const paginatedReports = reportList.slice((page - 1) * recordsPerPage, page * recordsPerPage);

  /**
   * Updates the current page when the user navigates via the {@link Pagination} control.
   *
   * @param _event - The synthetic change event (unused).
   * @param value - The newly selected page number.
   */
  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  /**
   * Updates the records-per-page state and resets the page to 1 when the user
   * changes the records-per-page selector.
   *
   * @param event - The MUI {@link SelectChangeEvent} carrying the new value.
   */
  const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newRecordsPerPage = Number(event.target.value);
    setRecordsPerPage(newRecordsPerPage);
    setPage(1);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box className="d-flex" sx={{ justifyContent: 'flex-end', width: '100%' }}>
        {recordLengthPicker(recordsPerPage, handleRecordsPerPageChange, t)}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          flex: 1,
        }}
      >
        {paginatedReports.map((report) => (
          <CardLayout key={report.id} data={report} onDownload={handleDownload} />
        ))}
      </Box>
      {reportList.length > 0 && (
        <Stack spacing={2} style={{ alignItems: 'center', padding: '0 0 45px' }}>
          <Pagination
            color="primary"
            count={pageCount}
            page={page}
            onChange={handlePaginationChange}
          />
        </Stack>
      )}
    </Box>
  );
};
